import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RequestService } from './request.service';

export type GlobalDetectionType = 'person_of_interest' | 'plate_of_interest';

@Injectable({
  providedIn: 'root'
})
export class GlobalDetectionService {
  public metadata = new BehaviorSubject<any>(undefined);

  constructor(private requestService: RequestService) { }

  private orgFilter(): any {
    return { 'organizationId._id': { $in: [this.requestService.orgId] } };
  }

  getMetadata(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.getMetaData('globaldetection', undefined, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        const results = data?.results || data;
        // Normalize to { fields: [...] } whether API returns that object or a bare fields array
        const normalized = Array.isArray(results)
          ? { fields: results }
          : (results?.fields ? results : { fields: results?.fields || [] });
        this.metadata.next(normalized);
        resolve(normalized);
      });
    });
  }

  list(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.getRequest('globaldetection', 'list', (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  getById(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.getSingleData('globaldetection', id, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data?.results ?? data);
      });
    });
  }

  search(pageIndex: number, pageSize: number, type: GlobalDetectionType): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('globaldetection', 'search', {
        // API uses 1-based page indexes (same as note/search)
        page: pageIndex + 1,
        count: pageSize,
        order: [{ field: 'createdAt', order: 'desc' }],
        fields: [
          'createdAt', 'name', 'notes', 'type', 'documents', 'imagePath',
          'person_of_interest_name', 'person_of_interest_gender',
          'plate_of_interest', 'car_of_interest_identifier', 'geolocation', 'location', 'address'
        ],
        filter: {
          $and: [
            this.orgFilter(),
            { type: { $eq: type } }
          ]
        }
      }, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  create(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('globaldetection', '', payload, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  update(id: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('globaldetection', id, payload, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  /** POST /globaldetection/update/{id} — update without touching updatedAt */
  updateWithoutTimestamp(id: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('globaldetection', 'update/' + id, payload, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  delete(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService.postRequest('globaldetection', id + '/delete', {}, (data, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(data);
      });
    });
  }

  searchPersons(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'persons/search', {
        order: [{ field: 'person_of_interest_name', order: 'asc' }],
        fields: ['person_of_interest_name'],
        term: term ? term.toLowerCase() : undefined,
        termfields: ['person_of_interest_name'],
        filter: { $and: [this.orgFilter()] }
      }, (data) => {
        resolve(data?.results || []);
      });
    });
  }

  getPerson(name: string): Promise<any> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'persons/get', {
        order: [{ field: 'updatedAt', order: 'desc' }],
        fields: [
          'createdAt', 'person_of_interest_name',
          'person_of_interest_gender', 'person_of_interest_hair_color', 'person_of_interest_dob',
          'person_of_interest_approximate_age', 'person_of_interest_height', 'person_of_interest_weight',
          'person_of_interest_eye_color', 'person_of_interest_race',
          'person_of_interest_distinguishing_features', 'person_of_interest_gender_other'
        ],
        term: '',
        termfields: ['person_of_interest_name'],
        filter: {
          person_of_interest_name: name,
          $and: [this.orgFilter()]
        }
      }, (data) => {
        resolve(data?.results);
      });
    });
  }

  searchCarPlates(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'carplate/search', {
        order: [{ field: 'plate_of_interest', order: 'asc' }],
        fields: ['plate_of_interest'],
        term: term ? term.toLowerCase() : undefined,
        termfields: ['plate_of_interest'],
        filter: { $and: [this.orgFilter()] }
      }, (data) => {
        resolve(data?.results || []);
      });
    });
  }

  getCarPlate(plate: string): Promise<any> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'carplate/get', {
        order: [{ field: 'updatedAt', order: 'desc' }],
        fields: [
          'createdAt', 'plate_of_interest', 'car_of_interest_identifier',
          'car_of_interest_description', 'car_color', 'car_make', 'car_model', 'car_type'
        ],
        term: '',
        termfields: ['plate_of_interest'],
        filter: {
          plate_of_interest: plate,
          $and: [this.orgFilter()]
        }
      }, (data) => {
        resolve(data?.results);
      });
    });
  }

  searchCarMakes(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'carmake/search', {
        order: [{ field: 'car_make', order: 'asc' }],
        fields: ['car_make'],
        term: term ? term.toLowerCase() : undefined,
        termfields: ['car_make'],
        filter: { $and: [this.orgFilter()] }
      }, (data) => {
        resolve(data?.results || []);
      });
    });
  }

  searchCarModels(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.requestService.postRequest('globaldetection', 'carmodel/search', {
        order: [{ field: 'car_model', order: 'asc' }],
        fields: ['car_model'],
        term: term ? term.toLowerCase() : undefined,
        termfields: ['car_model'],
        filter: { $and: [this.orgFilter()] }
      }, (data) => {
        resolve(data?.results || []);
      });
    });
  }
}
